import { HttpClient, HttpContext, HttpContextToken } from '@angular/common/http';
import { DestroyRef, Injectable, inject } from '@angular/core';

import { GBE_BASE_URL } from '@core/tokens/gbe-url.token';
import { convertKeysToSnakeCase } from '@core/utils/case-conversions';

import packageInfo from '../../../../../package.json';
import { BulkLogPayload, GfeLogLevel, QueuedLogEntry } from './gfe-log.types';
import { computeLogSignature } from './log-signature';

// Marks GfeLog requests so error/auth interceptors can opt out — we never want
// a failed log POST to trigger notifications, retries, or auth redirects (and
// crucially, we must not feed errors back into the global ErrorHandler loop).
export const GFE_LOG_REQUEST = new HttpContextToken<boolean>(() => false);

const FLUSH_INTERVAL_MS = 30_000;
const MAX_QUEUE_SIZE = 20;

@Injectable({
  providedIn: 'root'
})
export class GfeLogService {
  private readonly gbeUrl = inject(GBE_BASE_URL);
  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly endpoint = `${this.gbeUrl}/v4/gfe-logs/bulk`;

  private readonly queue = new Map<string, QueuedLogEntry>();
  private droppedDistinctSignatures = 0;
  private flushing = false;

  private readonly intervalHandle: ReturnType<typeof setInterval> | null;
  private readonly pageHideListener: (() => void) | null;

  constructor() {
    this.intervalHandle =
      typeof setInterval !== 'undefined'
        ? setInterval(() => this.flush(), FLUSH_INTERVAL_MS)
        : null;

    if (typeof window !== 'undefined') {
      this.pageHideListener = () => this.flushViaBeacon();
      window.addEventListener('pagehide', this.pageHideListener);
    } else {
      this.pageHideListener = null;
    }

    this.destroyRef.onDestroy(() => this.teardown());
  }

  sendLog(level: GfeLogLevel, data: unknown, metadata?: Record<string, unknown>): void {
    // Re-entrancy guard: a flush in progress that throws and feeds back into
    // the global ErrorHandler must not re-enqueue (would loop until dedup).
    if (this.flushing) return;

    try {
      const url = typeof window !== 'undefined' ? window.location.href : '';
      const signature = computeLogSignature(level, data, url);
      const now = new Date().toISOString();

      const existing = this.queue.get(signature);
      if (existing) {
        existing.count += 1;
        existing.lastOccurrenceAt = now;
        return;
      }

      if (this.queue.size >= MAX_QUEUE_SIZE) {
        this.droppedDistinctSignatures += 1;
        return;
      }

      this.queue.set(signature, {
        gfeVersion: packageInfo.version,
        level,
        data,
        url,
        metadata,
        count: 1,
        firstOccurrenceAt: now,
        lastOccurrenceAt: now
      });
    } catch {
      // Defensive: never let queue logic propagate an error to callers.
    }
  }

  private flush(): void {
    if (this.queue.size === 0 && this.droppedDistinctSignatures === 0) return;

    const payload = this.drainPayload();
    if (!payload) return;

    this.flushing = true;
    try {
      this.http
        .post<void>(this.endpoint, payload, {
          context: new HttpContext().set(GFE_LOG_REQUEST, true)
        })
        .subscribe({
          // Swallow: a failed log must never become a new error.
          error: () => {}
        });
    } catch {
      // Defensive: synchronous throw in the HTTP layer is dropped.
    } finally {
      this.flushing = false;
    }
  }

  // sendBeacon doesn't go through Angular's HttpClient → bypasses the
  // request-case interceptor, so we manually convert keys to snake_case to
  // keep the back-end DTO contract consistent with the regular flush path.
  private flushViaBeacon(): void {
    if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
      return;
    }
    if (this.queue.size === 0 && this.droppedDistinctSignatures === 0) return;

    const payload = this.drainPayload();
    if (!payload) return;

    try {
      const body = JSON.stringify(convertKeysToSnakeCase(payload));
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(this.endpoint, blob);
    } catch {
      // Best-effort: nothing actionable on unload path.
    }
  }

  private drainPayload(): BulkLogPayload | null {
    const logs = Array.from(this.queue.values());
    const dropped = this.droppedDistinctSignatures;

    this.queue.clear();
    this.droppedDistinctSignatures = 0;

    if (logs.length === 0 && dropped === 0) return null;

    return { logs, droppedDistinctSignatures: dropped };
  }

  private teardown(): void {
    if (this.intervalHandle !== null) clearInterval(this.intervalHandle);
    if (this.pageHideListener && typeof window !== 'undefined') {
      window.removeEventListener('pagehide', this.pageHideListener);
    }
  }
}
